import React from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { Button } from '../../ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Input } from '../../ui/input';
import { NumberInput } from '../../ui/number-input';
import { DateTimeInput } from '../../ui/date-time-input';
import { refuseIfDateInputIncomplete } from '../../../lib/dateInputs';
import { saisieDateIncomplete } from '../../../hooks/useAutoSave';
import AutoSaveIndicator from '../../ui/AutoSaveIndicator';
import type { AutoSave } from '../../../hooks/useAutoSave';
import { Label } from '../../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import type { Category, WOD } from '../../../types/heats';

export interface HeatFormValues {
  heat_number: string;
  heat_name: string;
  heat_date: string;
  start_time: string;
  end_time: string;
  max_participants: number;
  wod_id: string;
  category_id: string;
}

/** Champs obligatoires manquants → message sous le champ. */
export type HeatFormErrors = Partial<Record<'heat_number' | 'start_time' | 'max_participants', string>>;

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: 'create' | 'edit';
  form: HeatFormValues;
  errors?: HeatFormErrors;
  /**
   * Sauvegarde automatique — fournie en MODIFICATION seulement.
   *
   * En création il n'y a pas encore de ligne : écrire au blur supposerait de
   * créer une vague brouillon dès l'ouverture, donc une vague fantôme par
   * fenêtre abandonnée. La création garde son bouton.
   */
  auto?: AutoSave<Record<string, any>>;
  onChange: React.Dispatch<React.SetStateAction<HeatFormValues>>;
  wods: WOD[];
  categories: Category[];
  loading: boolean;
  /** Création seulement : en modification l'écriture se fait au blur. */
  onSubmit?: () => void;
  onCancel: () => void;
}


/**
 * Formulaire d'un heat, en création comme en modification.
 *
 * Les deux dialogs étaient deux copies littérales du même formulaire (126
 * lignes chacune), qui ne différaient que par leur titre et leur bouton de
 * validation — toute évolution d'un champ devait être faite deux fois, au
 * risque de ne l'être qu'une. `mode` porte cette seule différence.
 */
const HeatFormDialog: React.FC<Props> = ({
  open, onOpenChange, mode, form, errors = {}, onChange, wods, categories, loading, onSubmit, onCancel,
  auto,
}) => {
  const { t } = useTranslation();
  const isCreate = mode === 'create';
  const set = (patch: Partial<HeatFormValues>) => onChange(prev => ({ ...prev, ...patch }));
  const FieldError = ({ id }: { id: keyof HeatFormErrors }) =>
    errors[id] ? <p id={`heat-${id}-error`} className="text-xs text-destructive">{errors[id]}</p> : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {isCreate ? t('heats.createDialog.title') : t('heats.editDialog.title')}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="grid grid-cols-[100px,1fr] gap-3">
            <div className="space-y-2">
              <Label>{t('heats.createDialog.heatNumberRequired')}</Label>
              <NumberInput
                value={form.heat_number}
                aria-invalid={!!errors.heat_number}
                aria-describedby={errors.heat_number ? 'heat-heat_number-error' : undefined}
                className="aria-[invalid=true]:border-destructive"
                onChange={(e) => set({ heat_number: e.target.value })}
                onBlur={(e) => void auto?.commitIfChanged('heat_number', parseInt(e.target.value))}
              />
              <FieldError id="heat_number" />
            </div>
            <div className="space-y-2">
              <Label>{t('heats.createDialog.heatName')}</Label>
              <Input
                value={form.heat_name}
                onChange={(e) => set({ heat_name: e.target.value })}
                onBlur={(e) => void auto?.commitIfChanged('heat_name', e.target.value || null)}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>{t('heats.date')}</Label>
            <DateTimeInput
              type="date"
              value={form.heat_date}
              onChange={(e) => set({ heat_date: e.target.value })}
              onBlur={(e) => {
                if (saisieDateIncomplete(e)) return;
                void auto?.commitIfChanged('heat_date', e.target.value || null);
              }}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>{t('heats.createDialog.startTimeRequired')}</Label>
              <DateTimeInput
                type="time"
                value={form.start_time}
                aria-invalid={!!errors.start_time}
                aria-describedby={errors.start_time ? 'heat-start_time-error' : undefined}
                className="aria-[invalid=true]:border-destructive"
                onChange={(e) => set({ start_time: e.target.value })}
                onBlur={(e) => {
                  if (saisieDateIncomplete(e)) return;
                  void auto?.commitIfChanged('start_time', e.target.value || null);
                }}
              />
              <FieldError id="start_time" />
            </div>
            <div className="space-y-2">
              <Label>{t('heats.endTime')}</Label>
              <DateTimeInput
                type="time"
                value={form.end_time}
                onChange={(e) => set({ end_time: e.target.value })}
                onBlur={(e) => {
                  if (saisieDateIncomplete(e)) return;
                  void auto?.commitIfChanged('end_time', e.target.value || null);
                }}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>{t('heats.createDialog.wod')}</Label>
              <Select
                value={form.wod_id || 'none'}
                onValueChange={(v) => {
                  const avant = form.wod_id;
                  set({ wod_id: v === 'none' ? '' : v });
                  void auto?.commit({ wod_id: v === 'none' ? null : v }, () => set({ wod_id: avant }));
                }}
              >
                <SelectTrigger><SelectValue placeholder={t('heats.createDialog.noWod')} /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">{t('heats.createDialog.noWod')}</SelectItem>
                  {wods.map(wod => (
                    <SelectItem key={wod.id} value={wod.id}>{wod.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>{t('heats.createDialog.category')}</Label>
              <Select
                value={form.category_id || undefined}
                onValueChange={(v) => {
                  const avant = form.category_id;
                  set({ category_id: v });
                  void auto?.commit({ category_id: v }, () => set({ category_id: avant }));
                }}
              >
                {/* Pas de « toutes les catégories » : une heat porte une catégorie
                    (ou un groupe), la base refuse le reste. */}
                <SelectTrigger><SelectValue placeholder={t('heats.createDialog.chooseCategory')} /></SelectTrigger>
                <SelectContent>
                  {categories.map(cat => (
                    <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Le STATUT ne vit plus ici : il se règle sur la pastille de la
              ligne, au planning. Ce `Select` offrait sept valeurs, `running` et
              `finished` comprises — des états que le terrain écrit pendant la
              course — et il en proposait une (`rest`) que le serveur ne
              reconnaît même pas. Corriger une heure et publier la vague au
              planning public était le même clic. Voir HeatStatusChip.tsx. */}
          <div className="max-w-[50%] space-y-2">
            <Label>{t('heats.createDialog.maxParticipantsRequired')}</Label>
            <NumberInput
              value={form.max_participants}
              aria-invalid={!!errors.max_participants}
              aria-describedby={errors.max_participants ? 'heat-max_participants-error' : undefined}
              className="aria-[invalid=true]:border-destructive"
              onChange={(e) => set({ max_participants: parseInt(e.target.value) || 18 })}
              onBlur={(e) => void auto?.commitIfChanged('max_participants', parseInt(e.target.value) || 18)}
            />
            <FieldError id="max_participants" />
          </div>
        </div>

        <DialogFooter className={isCreate ? undefined : 'sm:justify-between'}>
          {isCreate ? (
            <>
              <Button variant="outline" onClick={onCancel}>
                {t('heats.actions.cancel')}
              </Button>
              <Button
                onClick={() => { if (refuseIfDateInputIncomplete(t)) return; onSubmit?.(); }}
                disabled={loading || !form.category_id}
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : t('heats.actions.create')}
              </Button>
            </>
          ) : (
            /* En modification l'écriture est déjà faite : « Annuler » n'aurait
               plus rien à annuler, il devient « Fermer ». */
            <>
              {auto && <AutoSaveIndicator status={auto.status} retry={auto.retry} />}
              <Button variant="outline" onClick={onCancel}>
                {t('common.close')}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default HeatFormDialog;
